import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { checkForOverride, glow, linearGradient } from "../helpers/theme";
import H1 from "../H1";
import H2 from "../H2";
import useGetMediaLocation from "../hooks/useGetMediaLocation";

let gridCols = [`3fr 1fr 1fr`, `1fr 3fr 1fr`, `1fr 1fr 3fr`];

export default function NowNextThen({ cue, cloud }) {
	const { projectId } = useParams();
	const [current, setCurrent] = useState(0);
	const selectedProject = useSelector(
		(state) => state?.main?.projects?.[projectId]
	);

	const baseTheme = useSelector((state) =>
		cloud
			? state?.projects?.[projectId + "_themes"]?.[cue.theme]
			: cue.theme
			? state?.main?.projects?.[projectId]?.themes?.[cue.theme]
			: state?.main?.projects?.[projectId]?.themes?.[selectedProject?.theme]
	);
	const baseThemeId = cue.theme ? cue.theme : selectedProject?.theme;

	useEffect(() => {
		let interval;

		if (["offair", "ready", "cuedon"].includes(cue.status)) {
			return setCurrent(0);
		} else {
			if (cue.count === "1") return setCurrent(0);
			if (cue.loop === "manual") return setCurrent(parseInt(cue.goTo || 0));

			interval = setInterval(() => {
				let c = current;
				switch (c) {
					case 0:
						setCurrent(1);
						break;

					case 1:
						if (parseInt(cue.count) <= 2) return setCurrent(0);
						setCurrent(2);
						break;

					case 2:
						// Endless loop mode
						if (cue.loop === "looping") return setCurrent(0);
						setCurrent(2);
						break;

					default:
						setCurrent(0);
						break;
				}
			}, [cue.timePerSection * 1000 || 5000]);
		}

		return () => {
			clearInterval(interval);
		};
	}, [cue.status, cue.loop, cue.timePerSection, current, cue.count, cue.goTo]);

	return (
		<div
			style={{
				gridTemplateColumns: gridCols[current],
			}}
			className="relative w-screen h-s4 transition-all duration-1000"
		>
			{cue.items?.map((item, index) => {
				if (index + 1 > parseInt(cue.count || 3)) return null;
				const {
					sectionTitle,
					title,
					description,
					image,
					url = "",
					imageFit = "cover",
				} = item;

				return (
					<NowNextThenSection
						cue={cue}
						key={index}
						index={index}
						current={current}
						sectionTitle={sectionTitle}
						title={title}
						description={description}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
						image={image}
						url={url}
						imageFit={imageFit}
					/>
				);
			})}
		</div>
	);
}

const NowNextThenSection = ({
	cue,
	index,
	current,
	sectionTitle,
	title,
	description,
	baseTheme,
	baseThemeId,
	image,
	url,
	imageFit,
}) => {
	let transformLeftActive = [];
	const mediaLocation = useGetMediaLocation();
	const cloud = useSelector((state) => state.rundown.cloud);

	switch (current) {
		case 0:
			transformLeftActive = [0, 60, 80];
			break;

		case 1:
			transformLeftActive = [0, 20, 80];
			break;

		case 2:
			transformLeftActive = [0, 20, 40];
			break;

		default:
			break;
	}
	return (
		<div
			style={{
				transform: `translateX(${transformLeftActive[index]}vw)`,
				zIndex: index,
				background: linearGradient(
					baseTheme,
					null,
					null,
					"background",
					"colour"
				),
			}}
			className="absolute inset-0 h-screen flex flex-col items-start transition-all duration-700 shadow-2xl"
		>
			<div
				style={{
					opacity: index === current ? `100%` : `10%`,
					gridTemplateRows: `1fr 4fr 1.5fr`,
				}}
				className="px-s6 py-s5 h-screen grid grid-rows-3 text-left transition-opacity duration-500"
			>
				<div className="text-s4xl font-semibold">
					<H1
						cue={cue}
						text={sectionTitle}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</div>
				{((image && image !== "none") || url) && (
					<img
						className={`w-s5 rounded-${checkForOverride(
							baseTheme,
							null,
							null,
							"background",
							"rounding"
						)}
						${imageFit === "cover" ? "object-cover h-s5" : "object-contain h-auto"}
						`}
						style={{
							boxShadow: glow(baseTheme, "now_next_then", null),
							borderTop: `${
								checkForOverride(
									baseTheme,
									null,
									null,
									"border",
									"sides"
								)[0] === "1"
									? baseTheme.border.size
									: 0
							}vw ${checkForOverride(
								baseTheme,
								null,
								null,
								"border",
								"style"
							)} ${checkForOverride(
								baseTheme,
								null,
								null,
								"border",
								"colour"
							)}`,
							borderBottom: `${
								checkForOverride(
									baseTheme,
									null,
									null,
									"border",
									"sides"
								)[1] === "1"
									? baseTheme.border.size
									: 0
							}vw ${checkForOverride(
								baseTheme,
								null,
								null,
								"border",
								"style"
							)} ${checkForOverride(
								baseTheme,
								null,
								null,
								"border",
								"colour"
							)}`,
							borderLeft: `${
								checkForOverride(
									baseTheme,
									null,
									null,
									"border",
									"sides"
								)[2] === "1"
									? baseTheme.border.size
									: 0
							}vw ${checkForOverride(
								baseTheme,
								null,
								null,
								"border",
								"style"
							)} ${checkForOverride(
								baseTheme,
								null,
								null,
								"border",
								"colour"
							)}`,
							borderRight: `${
								checkForOverride(
									baseTheme,
									null,
									null,
									"border",
									"sides"
								)[3] === "1"
									? baseTheme.border.size
									: 0
							}vw ${checkForOverride(
								baseTheme,
								null,
								null,
								"border",
								"style"
							)} ${checkForOverride(
								baseTheme,
								null,
								null,
								"border",
								"colour"
							)}`,
						}}
						alt={title}
						src={`${mediaLocation}${cloud ? url : image}`}
					/>
				)}
				<div className="flex flex-col items-start">
					<span className="pb-s2 text-s5xl">
						<H1
							cue={cue}
							text={title}
							baseTheme={baseTheme}
							baseThemeId={baseThemeId}
						/>
					</span>
					<span className="w-s5 text-left">
						<H2
							cue={cue}
							text={description}
							baseTheme={baseTheme}
							baseThemeId={baseThemeId}
							whitespace="whitespace-pre"
						/>
					</span>
				</div>
			</div>
		</div>
	);
};
